import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Prospect } from '../../types';

const GHL_PAYMENT_LINK = 'https://link.luminlending.com/payment-link/694098a8902dfc6c370a09d0';

const OnboardingForm: React.FC = () => {
  const [searchParams] = useSearchParams();

  // Check for token in URL first, then localStorage (for returning from payment)
  const urlToken = searchParams.get('token');
  const paymentSuccess = searchParams.get('payment') === 'success';
  const [token, setToken] = useState<string | null>(urlToken);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Personal Information
    legal_first_name: '',
    legal_last_name: '',
    preferred_first_name: '',
    cell_phone: '',
    personal_email: '',
    date_of_birth: '',
    nmls_number: '',
    dre_number: '',
    languages_spoken: '',

    // Step 2: Business Information
    business_ein: '',
    business_legal_name: '',
    business_type: '',
    business_address: '',
    irs_letter_url: '',

    // Step 3: Additional Information
    place_of_work: 'Remote' as 'Remote' | 'In Office',
    headshot_url: '',
    family_picture_url: '',
  });

  // Confirm payment with backend (creates profile, sends welcome email, etc.)
  const confirmPaymentWithBackend = async (paymentToken: string) => {
    setConfirmingPayment(true);
    setPaymentError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/confirm-payment`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: paymentToken }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Payment confirmation failed');
      }

      console.log('Payment confirmed successfully:', data);
      setShowPaymentSuccess(true);
    } catch (err: any) {
      console.error('Payment confirmation error:', err);
      // If already converted, show success anyway
      if (err.message?.includes('Already converted')) {
        setShowPaymentSuccess(true);
      } else {
        setPaymentError(err.message || 'Failed to confirm payment. Please contact support.');
      }
    } finally {
      setConfirmingPayment(false);
    }
  };

  useEffect(() => {
    // If returning from payment, restore token from localStorage and confirm with backend
    if (paymentSuccess && !urlToken) {
      const savedToken = localStorage.getItem('onboarding_token');
      if (savedToken) {
        setToken(savedToken);
        localStorage.removeItem('onboarding_token'); // Clean up
        // Call backend to confirm payment and create profile
        confirmPaymentWithBackend(savedToken);
        return;
      }
    }

    if (!token) {
      setError('No token provided. Please use the link from your email.');
      setLoading(false);
      return;
    }

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, paymentSuccess, urlToken]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target as Node)
      ) {
        setLanguageDropdownOpen(false);
      }
    };

    if (languageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [languageDropdownOpen]);

  const validateToken = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: funcError } = await supabase.functions.invoke('validate-prospect-token', {
        body: { token },
      });

      if (funcError) throw funcError;
      if (!data.success) throw new Error(data.error);

      setProspect(data.prospect);

      // Pre-populate form with prospect data and any existing onboarding data
      setFormData((prev) => ({
        ...prev,
        // Pre-fill from prospect
        legal_first_name: data.prospect.first_name,
        legal_last_name: data.prospect.last_name,
        personal_email: data.prospect.email || '',
        cell_phone: data.prospect.phone || '',
        nmls_number: data.prospect.nmls || '',

        // Pre-fill from onboarding data if it exists
        ...(data.onboarding_data && {
          preferred_first_name: data.onboarding_data.preferred_first_name || '',
          date_of_birth: data.onboarding_data.date_of_birth || '',
          nmls_number: data.onboarding_data.nmls_number || data.prospect.nmls || '',
          dre_number: data.onboarding_data.dre_number || '',
          languages_spoken: data.onboarding_data.languages_spoken || '',
          business_ein: data.onboarding_data.business_ein || '',
          business_legal_name: data.onboarding_data.business_legal_name || '',
          business_type: data.onboarding_data.business_type || '',
          business_address: data.onboarding_data.business_address || '',
          irs_letter_url: data.onboarding_data.irs_letter_url || '',
          place_of_work: data.onboarding_data.place_of_work || 'Remote',
          headshot_url: data.onboarding_data.headshot_url || '',
          family_picture_url: data.onboarding_data.family_picture_url || '',
        }),
      }));
    } catch (err: any) {
      // Show user-friendly error message instead of technical errors
      const errorMessage = err.message?.toLowerCase() || '';
      if (errorMessage.includes('expired')) {
        setError('This link has expired. Please contact your recruiter for a new link.');
      } else if (errorMessage.includes('not found') || errorMessage.includes('invalid')) {
        setError('This link is no longer valid. Please contact your recruiter for assistance.');
      } else {
        setError('This link is invalid or has expired. Please contact your recruiter for a new onboarding link.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (validationErrors.includes(name)) {
      setValidationErrors(validationErrors.filter(error => error !== name));
    }
  };

  const handleLanguageToggle = (language: string) => {
    const currentLanguages = formData.languages_spoken
      .split(', ')
      .filter(Boolean);

    const isSelected = currentLanguages.includes(language);

    let newLanguages: string[];
    if (isSelected) {
      newLanguages = currentLanguages.filter((lang) => lang !== language);
    } else {
      newLanguages = [...currentLanguages, language];
    }

    setFormData((prev) => ({
      ...prev,
      languages_spoken: newLanguages.join(', '),
    }));

    // Clear error for languages_spoken when user selects a language
    if (validationErrors.includes('languages_spoken') && newLanguages.length > 0) {
      setValidationErrors(validationErrors.filter(error => error !== 'languages_spoken'));
    }
  };

  const [uploadingField, setUploadingField] = useState<string | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const file = e.target.files?.[0];
    if (!file || !prospect || !token) return;

    setUploadingField(field);

    try {
      // Upload via edge function (bypasses RLS, validates token server-side)
      const formDataToSend = new FormData();
      formDataToSend.append('file', file);
      formDataToSend.append('token', token);
      formDataToSend.append('field', field);

      // Use fetch directly - supabase.functions.invoke doesn't handle FormData well
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/upload-onboarding-document`,
        {
          method: 'POST',
          body: formDataToSend,
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
        }
      );

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Upload failed');
      }

      setFormData((prev) => ({ ...prev, [field]: data.url }));

      // Clear error for this field when file is uploaded
      if (validationErrors.includes(field)) {
        setValidationErrors(validationErrors.filter(error => error !== field));
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      alert(err.message || 'Failed to upload file. Please try again.');
    } finally {
      setUploadingField(null);
    }
  };

  const validateCurrentStep = (): boolean => {
    const errors: string[] = [];

    if (currentStep === 1) {
      // Step 1: Personal Information
      if (!formData.legal_first_name) errors.push('legal_first_name');
      if (!formData.legal_last_name) errors.push('legal_last_name');
      if (!formData.cell_phone) errors.push('cell_phone');
      if (!formData.personal_email) errors.push('personal_email');
      if (!formData.date_of_birth) errors.push('date_of_birth');
      if (!formData.nmls_number) errors.push('nmls_number');
      if (!formData.languages_spoken) errors.push('languages_spoken');
    } else if (currentStep === 2) {
      // Step 2: Business Information
      if (!formData.business_ein) errors.push('business_ein');
      if (!formData.business_legal_name) errors.push('business_legal_name');
      if (!formData.business_type) errors.push('business_type');
      if (!formData.business_address) errors.push('business_address');
      if (!formData.irs_letter_url) errors.push('irs_letter_url');
    } else if (currentStep === 3) {
      // Step 3: Additional Information
      if (!formData.headshot_url) errors.push('headshot_url');
      if (!formData.family_picture_url) errors.push('family_picture_url');
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const hasError = (fieldName: string): boolean => {
    return validationErrors.includes(fieldName);
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setValidationErrors([]);
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const { data, error: funcError } = await supabase.functions.invoke('submit-onboarding-form', {
        body: {
          token,
          ...formData,
        },
      });

      if (funcError) throw funcError;
      if (!data.success) throw new Error(data.error);

      // Save token to localStorage before redirecting to payment
      if (token) {
        localStorage.setItem('onboarding_token', token);
      }

      // Redirect to GHL payment link with prefilled email
      const email = prospect?.company_email || prospect?.email || formData.personal_email;
      const paymentUrl = new URL(GHL_PAYMENT_LINK);
      if (email) {
        paymentUrl.searchParams.set('email', email);
      }
      if (formData.legal_first_name) {
        paymentUrl.searchParams.set('firstName', formData.legal_first_name);
      }
      if (formData.legal_last_name) {
        paymentUrl.searchParams.set('lastName', formData.legal_last_name);
      }
      window.location.href = paymentUrl.toString();
    } catch (err: any) {
      setError('Failed to submit form. Please try again or contact support.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb]">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full border-2 border-[#cfe5f0] border-t-lumin-primary animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-500">Validating your link...</p>
        </div>
      </div>
    );
  }

  // Confirming payment state (after returning from GHL)
  if (confirmingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb]">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <div className="h-16 w-16 rounded-full border-4 border-[#cfe5f0] border-t-lumin-primary animate-spin mx-auto"></div>
            <h3 className="mt-6 text-xl font-bold text-slate-900">Processing Your Payment</h3>
            <p className="mt-2 text-sm text-slate-600">
              Please wait while we set up your account...
            </p>
            <p className="mt-4 text-xs text-slate-400">
              This may take a few moments
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Payment confirmation error
  if (paymentError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb]">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-slate-900">Payment Confirmation Issue</h3>
            <p className="mt-2 text-sm text-slate-600">{paymentError}</p>
            <p className="mt-4 text-xs text-slate-400">
              If you completed payment, don't worry - your payment was received.
              Please email support@luminlending.com and we'll get your account set up.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !prospect) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb]">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-slate-900">Link Not Valid</h3>
            <p className="mt-2 text-sm text-slate-600">{error}</p>
            <p className="mt-4 text-xs text-slate-400">
              If you continue to have issues, email support@luminlending.com
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Payment success screen
  if (showPaymentSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb]">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
              <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="mt-4 text-2xl font-bold text-slate-900">Welcome to Lumin Lending!</h3>
            <p className="mt-2 text-sm text-slate-600">
              Your payment has been received and your account has been created.
            </p>
            <div className="mt-6 bg-slate-50 rounded-lg p-4 text-left">
              <p className="text-sm font-medium text-slate-900 mb-2">What happens next:</p>
              <ul className="text-sm text-slate-600 space-y-2">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Check your email for portal login instructions
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Complete W9 and banking information in the portal
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Follow the onboarding checklist to get fully set up
                </li>
              </ul>
            </div>
            <p className="mt-6 text-xs text-slate-400">
              Questions? Email support@luminlending.com
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f7fb] py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Welcome, {prospect.first_name}!</h1>
          <p className="mt-2 text-sm text-slate-600">
            Complete your onboarding to join Lumin Lending
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center">
            {[1, 2, 3, 4].map((step, index) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 font-semibold ${
                      currentStep >= step
                        ? 'bg-lumin-primary border-lumin-primary text-white'
                        : 'bg-white border-slate-300 text-slate-500'
                    }`}
                  >
                    {step}
                  </div>
                  <div className="mt-2 text-xs text-slate-600 font-medium">
                    {step === 1 && 'Personal'}
                    {step === 2 && 'Business'}
                    {step === 3 && 'Documents'}
                    {step === 4 && 'Tech Stack'}
                  </div>
                </div>
                {step < 4 && (
                  <div className="flex-1 h-1 -mt-6">
                    <div
                      className={`h-full ${
                        currentStep > step ? 'bg-lumin-primary' : 'bg-slate-300'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900">Personal Information</h2>

              {validationErrors.length > 0 && (
                <div className="rounded-md bg-red-50 p-4 border border-red-200">
                  <p className="text-sm text-red-800 font-medium">Please fill in all required fields before continuing.</p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    Legal First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="legal_first_name"
                    required
                    value={formData.legal_first_name}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm ${
                      hasError('legal_first_name') ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {hasError('legal_first_name') && (
                    <p className="mt-1 text-sm text-red-600">This field is required</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    Legal Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="legal_last_name"
                    required
                    value={formData.legal_last_name}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full rounded-md shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm ${
                      hasError('legal_last_name') ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {hasError('legal_last_name') && (
                    <p className="mt-1 text-sm text-red-600">This field is required</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Preferred First Name
                </label>
                <input
                  type="text"
                  name="preferred_first_name"
                  value={formData.preferred_first_name}
                  onChange={handleInputChange}
                  placeholder="What should we call you?"
                  className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm"
                />
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    Cell Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="cell_phone"
                    required
                    value={formData.cell_phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    className={`mt-1 block w-full rounded-md shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm ${
                      hasError('cell_phone') ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {hasError('cell_phone') && (
                    <p className="mt-1 text-sm text-red-600">This field is required</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    Personal Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="personal_email"
                    required
                    value={formData.personal_email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className={`mt-1 block w-full rounded-md shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm ${
                      hasError('personal_email') ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {hasError('personal_email') && (
                    <p className="mt-1 text-sm text-red-600">This field is required</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  required
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm ${
                    hasError('date_of_birth') ? 'border-red-500' : 'border-slate-300'
                  }`}
                />
                {hasError('date_of_birth') && (
                  <p className="mt-1 text-sm text-red-600">This field is required</p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    NMLS # <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nmls_number"
                    required
                    value={formData.nmls_number}
                    onChange={handleInputChange}
                    placeholder="123456"
                    className={`mt-1 block w-full rounded-md shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm ${
                      hasError('nmls_number') ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {hasError('nmls_number') && (
                    <p className="mt-1 text-sm text-red-600">This field is required</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    CA DRE #
                  </label>
                  <input
                    type="text"
                    name="dre_number"
                    value={formData.dre_number}
                    onChange={handleInputChange}
                    placeholder="01234567"
                    className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Languages Spoken <span className="text-red-500">*</span>
                </label>
                <div className="mt-1 relative" ref={languageDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                    className={`w-full bg-white rounded-md shadow-sm px-4 py-2 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-lumin-primary sm:text-sm flex justify-between items-center ${
                      hasError('languages_spoken') ? 'border-2 border-red-500' : 'border border-slate-300 focus:border-lumin-primary'
                    }`}
                  >
                    <span className="truncate">
                      {formData.languages_spoken || 'Select languages...'}
                    </span>
                    <svg
                      className={`ml-2 h-5 w-5 text-slate-400 transition-transform ${
                        languageDropdownOpen ? 'transform rotate-180' : ''
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {languageDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {[
                        'English',
                        'Spanish',
                        'German',
                        'Japanese',
                        'French',
                        'Portuguese',
                        'Russian',
                        'Italian',
                        'Dutch',
                        'Polish',
                        'Turkish',
                        'Persian',
                        'Chinese',
                        'Vietnamese',
                        'Indonesian',
                        'Czech',
                        'Korean',
                        'Ukrainian',
                      ].map((language) => {
                        const isSelected = formData.languages_spoken
                          .split(', ')
                          .filter(Boolean)
                          .includes(language);

                        return (
                          <label
                            key={language}
                            className="flex items-center px-4 py-2 hover:bg-slate-100 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleLanguageToggle(language)}
                              className="h-4 w-4 text-lumin-primary focus:ring-lumin-primary border-slate-300 rounded"
                            />
                            <span className="ml-3 text-sm text-slate-700">{language}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                {formData.languages_spoken && (
                  <p className="mt-1 text-xs text-slate-500">
                    Selected: {formData.languages_spoken}
                  </p>
                )}
                {hasError('languages_spoken') && (
                  <p className="mt-1 text-sm text-red-600">This field is required</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Business Information */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900">Business Information</h2>

              {validationErrors.length > 0 && (
                <div className="rounded-md bg-red-50 p-4 border border-red-200">
                  <p className="text-sm text-red-800 font-medium">Please fill in all required fields before continuing.</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Business EIN <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="business_ein"
                  required
                  value={formData.business_ein}
                  onChange={handleInputChange}
                  placeholder="12-3456789"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm ${
                    hasError('business_ein') ? 'border-red-500' : 'border-slate-300'
                  }`}
                />
                {hasError('business_ein') && (
                  <p className="mt-1 text-sm text-red-600">This field is required</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Personal Business Name <span className="text-red-500">*</span>
                </label>
                <p className="mt-1 text-xs text-slate-500">
                  Enter the exact legal business name, as registered with the EIN
                </p>
                <input
                  type="text"
                  name="business_legal_name"
                  required
                  value={formData.business_legal_name}
                  onChange={handleInputChange}
                  placeholder="Company Name LLC"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm ${
                    hasError('business_legal_name') ? 'border-red-500' : 'border-slate-300'
                  }`}
                />
                {hasError('business_legal_name') && (
                  <p className="mt-1 text-sm text-red-600">This field is required</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Business Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="business_type"
                  required
                  value={formData.business_type}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm ${
                    hasError('business_type') ? 'border-red-500' : 'border-slate-300'
                  }`}
                >
                  <option value="">Select business type...</option>
                  <option value="Corporation">Corporation</option>
                  <option value="Limited Liability Company (LLC)">Limited Liability Company (LLC)</option>
                  <option value="Partnership">Partnership</option>
                </select>
                {hasError('business_type') && (
                  <p className="mt-1 text-sm text-red-600">This field is required</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Business Address <span className="text-red-500">*</span>
                </label>
                <p className="mt-1 text-xs text-slate-500">
                  Enter exact address as on EIN letter (or IRS-updated address if different)
                </p>
                <input
                  type="text"
                  name="business_address"
                  required
                  value={formData.business_address}
                  onChange={handleInputChange}
                  placeholder="123 Main St, City, State 12345"
                  className={`mt-1 block w-full rounded-md shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm ${
                    hasError('business_address') ? 'border-red-500' : 'border-slate-300'
                  }`}
                />
                {hasError('business_address') && (
                  <p className="mt-1 text-sm text-red-600">This field is required</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  IRS Letter <span className="text-red-500">*</span>
                </label>
                <p className="mt-1 text-xs text-slate-500">
                  Upload your IRS letter showing your EIN and business name
                </p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(e, 'irs_letter_url')}
                  required={!formData.irs_letter_url}
                  disabled={uploadingField === 'irs_letter_url'}
                  className={`mt-2 block w-full text-sm text-slate-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-lumin-primary file:text-white
                    hover:file:bg-lumin-primary/90
                    disabled:opacity-50 disabled:cursor-not-allowed ${
                      hasError('irs_letter_url') ? 'border-2 border-red-500 rounded-md p-2' : ''
                    }`}
                />
                {uploadingField === 'irs_letter_url' && (
                  <p className="mt-1 text-sm text-blue-600 flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </p>
                )}
                {formData.irs_letter_url && !uploadingField && (
                  <p className="mt-1 text-sm text-green-600">File uploaded successfully</p>
                )}
                {hasError('irs_letter_url') && (
                  <p className="mt-1 text-sm text-red-600">Please upload your IRS Letter</p>
                )}

                {/* IRS Letter Example Reference */}
                <div className="mt-3 p-4 bg-slate-50 border border-slate-300 rounded-lg">
                  <p className="text-xs font-semibold text-slate-800 mb-3">
                    Example - What your IRS letter should look like:
                  </p>

                  <div className="bg-white border-2 border-slate-400 rounded p-4 text-xs font-mono space-y-2">
                    <div className="border-b border-slate-300 pb-2">
                      <p className="font-bold">Department of the Treasury</p>
                      <p className="font-bold">Internal Revenue Service</p>
                    </div>

                    <div className="mt-3 space-y-1">
                      <p className="text-slate-600">Employer Identification Number:</p>
                      <p className="font-bold bg-yellow-100 inline-block px-2 py-1 border border-yellow-400">
                        12-3456789
                      </p>
                    </div>

                    <div className="mt-3 space-y-1">
                      <p className="text-slate-600">Business Legal Name:</p>
                      <p className="font-bold bg-yellow-100 inline-block px-2 py-1 border border-yellow-400">
                        COMPANY NAME LLC
                      </p>
                    </div>

                    <div className="mt-3 space-y-1">
                      <p className="text-slate-600">Business Address:</p>
                      <p className="font-bold bg-yellow-100 inline-block px-2 py-1 border border-yellow-400">
                        123 MAIN ST, CITY, STATE 12345
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-slate-600 mt-3">
                    <span className="font-semibold">Note:</span> The highlighted sections (EIN, Business Name, and Address) must match what you entered above.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Documents & Additional Info */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900">Additional Information</h2>

              {validationErrors.length > 0 && (
                <div className="rounded-md bg-red-50 p-4 border border-red-200">
                  <p className="text-sm text-red-800 font-medium">Please upload all required images before continuing.</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Work Location <span className="text-red-500">*</span>
                </label>
                <select
                  name="place_of_work"
                  required
                  value={formData.place_of_work}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-lumin-primary focus:ring-lumin-primary sm:text-sm"
                >
                  <option value="Remote">Remote</option>
                  <option value="In Office">In Office</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Professional Headshot <span className="text-red-500">*</span>
                </label>
                <p className="mt-1 text-xs text-slate-500">
                  If your file is over 500 KB, compress it here:{' '}
                  <a
                    href="https://imagecompressor.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lumin-primary hover:underline"
                  >
                    imagecompressor.com
                  </a>
                </p>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(e, 'headshot_url')}
                  required={!formData.headshot_url}
                  disabled={uploadingField === 'headshot_url'}
                  className={`mt-1 block w-full text-sm text-slate-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-lumin-primary file:text-white
                    hover:file:bg-lumin-primary/90
                    disabled:opacity-50 disabled:cursor-not-allowed ${
                      hasError('headshot_url') ? 'border-2 border-red-500 rounded-md p-2' : ''
                    }`}
                />
                {uploadingField === 'headshot_url' && (
                  <p className="mt-1 text-sm text-blue-600 flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </p>
                )}
                {formData.headshot_url && !uploadingField && (
                  <p className="mt-1 text-sm text-green-600">File uploaded successfully</p>
                )}
                {hasError('headshot_url') && (
                  <p className="mt-1 text-sm text-red-600">Please upload your professional headshot</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Family Picture <span className="text-red-500">*</span>
                </label>
                <p className="mt-1 text-xs text-slate-500">
                  Upload quirky/memorable image (e.g., Kevin's example). If your file is over 500 KB, compress it here:{' '}
                  <a
                    href="https://imagecompressor.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lumin-primary hover:underline"
                  >
                    imagecompressor.com
                  </a>
                </p>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload(e, 'family_picture_url')}
                  required={!formData.family_picture_url}
                  disabled={uploadingField === 'family_picture_url'}
                  className={`mt-1 block w-full text-sm text-slate-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-lumin-primary file:text-white
                    hover:file:bg-lumin-primary/90
                    disabled:opacity-50 disabled:cursor-not-allowed ${
                      hasError('family_picture_url') ? 'border-2 border-red-500 rounded-md p-2' : ''
                    }`}
                />
                {uploadingField === 'family_picture_url' && (
                  <p className="mt-1 text-sm text-blue-600 flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </p>
                )}
                {formData.family_picture_url && !uploadingField && (
                  <p className="mt-1 text-sm text-green-600">File uploaded successfully</p>
                )}
                {hasError('family_picture_url') && (
                  <p className="mt-1 text-sm text-red-600">Please upload your family picture</p>
                )}

                {/* Kevin's Example */}
                <div className="mt-3 p-3 bg-slate-50 border border-slate-300 rounded-lg">
                  <p className="text-xs font-semibold text-slate-800 mb-2">
                    Example - Kevin's quirky family photo:
                  </p>
                  <img
                    src="/family-picture-example.png"
                    alt="Kevin's family dressed as Mario characters"
                    className="w-full max-w-md mx-auto rounded border-2 border-slate-300"
                  />
                  <p className="text-xs text-slate-600 mt-2">
                    Have fun with it! Show your personality with a creative family photo.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Tech Stack */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900">Tech Stack</h2>

              <div className="bg-slate-50 rounded-lg p-6">
                <h3 className="text-sm font-medium text-slate-900 mb-4">Please review your information:</h3>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Legal Name:</span> {formData.legal_first_name} {formData.legal_last_name}
                  </div>
                  <div>
                    <span className="font-medium">Preferred Name:</span> {formData.preferred_first_name || 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium">Email:</span> {formData.personal_email}
                  </div>
                  <div>
                    <span className="font-medium">Phone:</span> {formData.cell_phone}
                  </div>
                  <div>
                    <span className="font-medium">DOB:</span> {formData.date_of_birth}
                  </div>
                  <div>
                    <span className="font-medium">NMLS:</span> {formData.nmls_number}
                  </div>
                  <div>
                    <span className="font-medium">Languages:</span> {formData.languages_spoken}
                  </div>
                  <div>
                    <span className="font-medium">CA DRE:</span> {formData.dre_number || 'N/A'}
                  </div>
                  <div className="col-span-2">
                    <span className="font-medium">Business Name:</span> {formData.business_legal_name}
                  </div>
                  <div>
                    <span className="font-medium">Business Type:</span> {formData.business_type}
                  </div>
                  <div>
                    <span className="font-medium">EIN:</span> {formData.business_ein}
                  </div>
                  <div className="col-span-2">
                    <span className="font-medium">Business Address:</span> {formData.business_address}
                  </div>
                  <div>
                    <span className="font-medium">Work Location:</span> {formData.place_of_work}
                  </div>
                  <div>
                    <span className="font-medium">IRS Letter:</span> {formData.irs_letter_url ? 'Uploaded' : 'Not uploaded'}
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="bg-white border-2 border-lumin-primary rounded-lg p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Lumin Tech Stack Subscription</h3>
                <p className="text-sm text-slate-700 mb-4">
                  CRM (Automation & Communication) + ARIVE (LOS)
                </p>

                <div className="border-t border-slate-300 pt-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-slate-900">Lumin Tech Stack Subscription</p>
                        <p className="text-xs text-slate-600">Monthly Billing</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">$300/month</p>
                        <p className="text-xs text-slate-600">Due in 30 days</p>
                      </div>
                    </div>

                    <div className="flex justify-between items-start border-t border-slate-200 pt-3">
                      <div>
                        <p className="font-semibold text-slate-900">Setup Fee (One Time)</p>
                        <p className="text-xs text-slate-600">One-Time Billing</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">$300</p>
                        <p className="text-xs text-slate-600">Due Today</p>
                      </div>
                    </div>

                    <div className="flex justify-between items-start border-t border-slate-200 pt-3">
                      <div>
                        <p className="font-semibold text-slate-900">A2P Fast Track + CRM Number</p>
                        <p className="text-xs text-slate-600">One-Time Billing</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-900">$20-30</p>
                        <p className="text-xs text-slate-600">Due in 5-7 days</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-300">
                  <p className="text-xs text-slate-600 italic">
                    <strong>Note:</strong> CRM platform monthly usage fees (similar to token-based usage) billed directly to the Loan Officer's CRM account wallet.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={submitting}
                className="px-6 py-2 border border-slate-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
              >
                Back
              </button>
            )}

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={handleNext}
                className="ml-auto px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-lumin-primary hover:bg-lumin-primary/90"
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="ml-auto px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-lumin-primary hover:bg-lumin-primary/90 disabled:opacity-50"
              >
                {submitting ? 'Processing...' : 'Continue to Tech Stack'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingForm;
